import * as Yup from 'yup'
import { ValidationError } from 'yup'
import { isFuture } from 'date-fns'
import i18next from 'i18next'

import store, { Lead } from 'store'
import { CustomAddressFieldAccessor } from 'store/types/settings'
import { CheckLeadDuplicatesV2Payload, CheckLeadDuplicatesV2Response, LeadDuplicateReason } from 'services/types/lead'
import { checkLeadDuplicatesV2 } from 'services/lead'
import { LeadDetails } from 'types/global'
import { urlRegex } from 'utils/regex'
import {
  getValidationMessages,
  getMobileNumberValidation,
  containsValidCharactersAndPeriodOnly,
  isValidPostCode,
  EmailValidationFn,
  ContactNameValidation,
} from 'utils/validation'
import { FETCHING_STATUS, FIELD_TYPE_ACCESSOR } from 'utils/constants'
import { DUPLICATE_MESSAGE, INVALID_CONTACT_PERSON } from 'modules/Leads/NewLead/constants'

const {
  REQUIRED_ADDRESS,
  REQUIRED_FIELD,
  INVALID_CHARACTERS,
  REQUIRED_COUNTRY,
  INVALID_URL,
  INVALID_NUM_EMPLOYEES,
  LATITUDE_TYPE_ERROR,
  LONGITUDE_TYPE_ERROR,
  REQUIRED_EMAIL,
  REQUIRED_PHONE_NUMBER,
  REQUIRED_LEAD_TYPE,
  INVALID_LEAD_IN_DATE_FUTURE,
  REQUIRED_LEAD_IN_DATE,
  REQUIRED_SALES_PIPELINE,
  REQUIRED_LEAD_SOURCE,
  REQUIRED_LEAD_CAMPAIGN,
  REQUIRED_CUSTOM_FIELD,
  REQUIRED_COMPANY,
  REQUIRED_REGISTRATION_NUMBER,
} = getValidationMessages()

export const LeadAddressValidationSchema = (addressFields: CustomAddressFieldAccessor[]) => {
  const postCodeRule = Yup.string()
    .nullable()
    .test(
      'isValidPostCode',
      i18next.t('validation_number_and_hypens', { name: i18next.t('postal_code_lbl') }),
      (value) => !value || isValidPostCode(value),
    )

  const addressObjectShape = {
    addressLine1: Yup.string().nullable(),
    addressLine2: Yup.string().nullable(),
    city: Yup.string().nullable(),
    country: Yup.string(),
    postalCode: postCodeRule,
    province: Yup.string().nullable(),
    suburb: Yup.string().nullable(),
    ward: Yup.string().nullable(),
    lat: Yup.number().typeError(LATITUDE_TYPE_ERROR).nullable(),
    lng: Yup.number().typeError(LONGITUDE_TYPE_ERROR).nullable(),
  }

  addressFields.forEach((accessor) => {
    switch (accessor) {
      case 'addressLine1':
        addressObjectShape[accessor] = Yup.string().nullable().required(REQUIRED_ADDRESS)
        break
      case 'city':
        addressObjectShape[accessor] = Yup.string()
          .nullable()
          .test('containsValidCharactersOnly', INVALID_CHARACTERS, (value) =>
            containsValidCharactersAndPeriodOnly(value),
          )
        break
      case 'country':
        addressObjectShape[accessor] = Yup.string()
          .required(REQUIRED_COUNTRY)
          .test('containsValidCharactersOnly', INVALID_CHARACTERS, (value) =>
            containsValidCharactersAndPeriodOnly(value),
          )
        break
      case 'postalCode':
        addressObjectShape[accessor] = postCodeRule
        break
      case 'province':
        addressObjectShape[accessor] = Yup.string()
          .nullable()
          .test('containsValidCharactersOnly', INVALID_CHARACTERS, (value) =>
            containsValidCharactersAndPeriodOnly(value),
          )
        break
      case 'suburb':
        addressObjectShape[accessor] = Yup.string()
          .nullable()
          .test('containsValidCharactersOnly', INVALID_CHARACTERS, (value) =>
            containsValidCharactersAndPeriodOnly(value),
          )
        break
      case 'ward':
        addressObjectShape[accessor] = Yup.string()
          .nullable()
          .test('containsValidCharactersOnly', INVALID_CHARACTERS, (value) =>
            containsValidCharactersAndPeriodOnly(value),
          )
        break
    }
  })

  return Yup.object().shape(addressObjectShape)
}

export const CompanyInfoValidationSchema = (requiredFields: string[] = []) => {
  const baseSchema = {
    annualRevenue: Yup.number(),
    industryId: Yup.string().ensure(),
    name: Yup.string().ensure(),
    numEmployees: Yup.number().integer(INVALID_NUM_EMPLOYEES),
    website: Yup.string().nullable().matches(urlRegex, INVALID_URL),
  }

  return Yup.object().shape({
    ...baseSchema,
    name: requiredFields.includes('company_info.name')
      ? Yup.string().nullable().required(REQUIRED_COMPANY).ensure()
      : Yup.string().ensure(),
    registrationNumber: requiredFields.includes('company_info.registration_number')
      ? Yup.string().nullable().required(REQUIRED_REGISTRATION_NUMBER).ensure()
      : Yup.string().ensure(),
  })
}

export const LeadContactValidationSchema = Yup.object().shape(
  {
    fname: ContactNameValidation,
    lname: ContactNameValidation,
    email: Yup.array().of(EmailValidationFn()),
    mobile: Yup.array().of(getMobileNumberValidation()),
  },
  [['email', 'mobile']],
)

export const checkLeadDuplicate = async function (values: LeadDetails.LeadDetailsForm, scopes: LeadDuplicateReason[]) {
  const { contactPerson } = values

  const payload = GetPayload(values, scopes)
  const response = await checkLeadDuplicatesV2(payload)

  if (response.status === FETCHING_STATUS.fail) {
    return true
  }

  if (response.status === FETCHING_STATUS.success) {
    const data: CheckLeadDuplicatesV2Response = response.data
    if (!data.reasons) {
      store.dispatch(Lead.clearLeadDuplicate())
      return true
    }

    store.dispatch(Lead.setLeadDuplicate(data))
    const errors: ValidationError[] = []

    contactPerson?.email?.forEach((em, i) => {
      const entry = data.reasons?.email_address?.find((item) => item.value === em.value && item.status === 'exact')
      if (entry) {
        const msg = DUPLICATE_MESSAGE.email.exact
        const value = em.value
        const path = `contactPerson.email[${i}].value`
        errors.push(new ValidationError(msg, value, path))
      }
    })

    contactPerson?.mobile?.forEach((mb, i) => {
      const entry = data.reasons?.mobile?.find((item) => item.value === mb.value && item.status === 'exact')
      if (entry) {
        const msg = DUPLICATE_MESSAGE.mobile.exact
        const value = mb.value
        const path = `contactPerson.mobile[${i}].value`
        errors.push(new ValidationError(msg, value, path))
      }
    })

    if (data.reasons?.company_name === 'exact') {
      const msg = DUPLICATE_MESSAGE.companyName.exact
      const value = values.companyInfo.name
      const path = 'companyInfo.name'
      errors.push(new ValidationError(msg, value, path))
    }
    if (data.reasons?.reg_no === 'exact') {
      const msg = DUPLICATE_MESSAGE.registrationNumber.exact
      const value = values.companyInfo.registrationNumber
      const path = 'companyInfo.registrationNumber'
      errors.push(new ValidationError(msg, value, path))
    }
    if (data.reasons?.website === 'exact') {
      const msg = DUPLICATE_MESSAGE.website.exact
      const value = values.companyInfo.website
      const path = 'companyInfo.website'
      errors.push(new ValidationError(msg, value, path))
    }

    if (errors.length > 0) {
      // Refer to the documentation this function should receive an array, but it shows type error
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore:next-line
      return new ValidationError(errors)
    }
  }

  return true
}

const NewLeadCustomFieldsValidationSchema = Yup.array().of(
  Yup.object().shape({
    customLeadFormFieldId: Yup.string().ensure(),
    custom_lead_form_field_type_accessor: Yup.string().ensure(),
    value: Yup.mixed()
      // validate single select
      .when(['is_active', 'is_required', 'type_accessor'], {
        is: (isActive: boolean, isRequired: boolean, type_accessor: string) =>
          isActive && isRequired && type_accessor === FIELD_TYPE_ACCESSOR.singleSelect,
        then: Yup.object({
          label: Yup.string(),
          value: Yup.string().required(REQUIRED_CUSTOM_FIELD),
        }).required(REQUIRED_CUSTOM_FIELD),
      })
      // validate lookup
      .when(['is_active', 'is_required', 'type_accessor'], {
        is: (isActive: boolean, isRequired: boolean, type_accessor: string) =>
          isActive && isRequired && type_accessor === FIELD_TYPE_ACCESSOR.lookup,
        then: Yup.object({
          leadId: Yup.string().ensure().required(REQUIRED_CUSTOM_FIELD),
          leadName: Yup.string().ensure(),
        }),
      })
      // validate multiple select
      .when(['is_active', 'is_required', 'type_accessor'], {
        is: (isActive: boolean, isRequired: boolean, type_accessor: string) =>
          isActive && isRequired && type_accessor === FIELD_TYPE_ACCESSOR.multiSelect,
        then: Yup.array()
          .required(REQUIRED_CUSTOM_FIELD)
          .of(
            Yup.object({
              label: Yup.string().ensure(),
              value: Yup.string().ensure().required(REQUIRED_CUSTOM_FIELD),
            }).required(REQUIRED_CUSTOM_FIELD),
          ),
      })
      // validate others
      .when(['is_active', 'is_required', 'type_accessor'], {
        is: (isActive: boolean, isRequired: boolean, type_accessor: string) =>
          isActive &&
          isRequired &&
          type_accessor !== FIELD_TYPE_ACCESSOR.singleSelect &&
          type_accessor !== FIELD_TYPE_ACCESSOR.multiSelect &&
          type_accessor !== FIELD_TYPE_ACCESSOR.lookup,
        then: Yup.string().required(REQUIRED_CUSTOM_FIELD),
      }),
  }),
)

const GetPayload = (values: LeadDetails.LeadDetailsForm, scopes: LeadDuplicateReason[]) => {
  const { contactPerson, companyInfo } = values
  const payload: CheckLeadDuplicatesV2Payload = {
    lead_id: values.leadId,
    fields: {
      fname: contactPerson.fname || '',
      lname: contactPerson.lname || '',
      email_address: contactPerson.email.filter((i) => i.value?.length > 0).map((i) => i.value) || [],
      mobile: contactPerson.mobile.filter((i) => i.value?.length > 0).map((i) => i.value) || [],
      company_name: companyInfo.name || '',
      reg_no: companyInfo.registrationNumber || '',
      website: companyInfo.website || '',
    },
    scopes,
  }
  return payload
}

const contactPersonFieldHasValue = (fieldKey: keyof typeof INVALID_CONTACT_PERSON) => (
  values: LeadDetails.LeadDetailsForm,
) => values.contactPerson?.[fieldKey].some((item) => item.value && item.value.trim() !== '')

export const customContactPersonValidation = (fieldKey: keyof typeof INVALID_CONTACT_PERSON) => (
  values: LeadDetails.LeadDetailsForm,
) => {
  const validationErrorMessage = INVALID_CONTACT_PERSON[fieldKey]
  const hasValue = contactPersonFieldHasValue(fieldKey)(values)
  const errors: ValidationError[] = []

  // Verify all mobile fields have value
  if (hasValue) {
    values.contactPerson[fieldKey].forEach((i, index) => {
      if (!i.value || i.value.trim() === '') {
        errors.push(new ValidationError(validationErrorMessage, i.value, `contactPerson.${fieldKey}[${index}].value`))
      }
    })
  }

  if (errors.length > 0) {
    // Refer to the documentation this function should receive an array, but it shows type error
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore:next-line
    return new ValidationError(errors)
  }

  return true
}

export const requiredEmailOrMobile = (values: LeadDetails.LeadDetailsForm) => {
  const errors: ValidationError[] = []

  const hasEmail = contactPersonFieldHasValue('email')(values)
  const hasMobile = contactPersonFieldHasValue('mobile')(values)

  if (!hasEmail && !hasMobile) {
    errors.push(
      new ValidationError(REQUIRED_EMAIL, values.contactPerson.email[0].value, 'contactPerson.email[0].value'),
    )
    errors.push(
      new ValidationError(REQUIRED_PHONE_NUMBER, values.contactPerson.mobile[0].value, 'contactPerson.mobile[0].value'),
    )

    // Refer to the documentation this function should receive an array, but it shows type error
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore:next-line
    return new ValidationError(errors)
  }

  return true
}

const NewLeadDetailsSchema = (addressFields: CustomAddressFieldAccessor[], requiredFields: string[]) =>
  Yup.object()
    .shape({
      leadTypeId: Yup.number().required(REQUIRED_LEAD_TYPE),
      leadInDate: Yup.string()
        .required(REQUIRED_LEAD_IN_DATE)
        .test('is-future', INVALID_LEAD_IN_DATE_FUTURE, (value: string) => !isFuture(new Date(value))),
      leadSourceId: Yup.string().required(REQUIRED_LEAD_SOURCE),
      leadCampaignId: requiredFields.includes('lead_campaign_id')
        ? Yup.string().nullable().required(REQUIRED_LEAD_CAMPAIGN).ensure()
        : Yup.string().ensure(),
      leadProcessId: Yup.string().required(REQUIRED_SALES_PIPELINE),
      address: Yup.array().of(LeadAddressValidationSchema(addressFields)),
      contactPerson: LeadContactValidationSchema,
      companyInfo: CompanyInfoValidationSchema(requiredFields),
      customLeadFormFields: NewLeadCustomFieldsValidationSchema,
    })
    .test('requiredEmailOrMobile', 'msg', requiredEmailOrMobile)
    .test('customEmailValidation', 'msg', customContactPersonValidation('email'))
    .test('customMobileValidation', 'msg', customContactPersonValidation('mobile'))

export default NewLeadDetailsSchema
